import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Shield, Plus, Edit, Trash2, Lock, Save } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: {}
  });

  useEffect(() => {
    fetchRoles();
    fetchAvailablePermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
      setIsLoading(false);
    } catch (error) {
      toast.error('Failed to load roles');
      setIsLoading(false);
    }
  };

  const fetchAvailablePermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/permissions/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailablePermissions(response.data);
    } catch (error) {
      console.error('Failed to load permissions', error);
    }
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      // Editing existing role
      setEditingRole(role);
      setFormData({
        name: role.name,
        display_name: role.display_name,
        description: role.description || '',
        permissions: role.permissions || {}
      });
    } else {
      // Creating new role
      setEditingRole(null);
      // Initialize with all permissions set to false
      const initialPermissions = {};
      Object.values(availablePermissions).forEach(group => {
        Object.keys(group).forEach(perm => {
          initialPermissions[perm] = false;
        });
      });
      setFormData({
        name: '',
        display_name: '',
        description: '',
        permissions: initialPermissions
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');

      if (editingRole) {
        // Update existing role
        await axios.put(`${API}/roles/${editingRole.id}`, {
          display_name: formData.display_name,
          description: formData.description,
          permissions: formData.permissions
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Role updated successfully!');
      } else {
        // Create new role
        await axios.post(`${API}/roles`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Role created successfully!');
      }

      setDialogOpen(false);
      fetchRoles();
      setFormData({ name: '', display_name: '', description: '', permissions: {} });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDelete = async (roleId, roleName) => {
    if (!confirm(`Are you sure you want to delete the role "${roleName}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Role deleted successfully!');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const togglePermission = (permissionKey) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permissionKey]: !prev.permissions[permissionKey]
      }
    }));
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading roles...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Toaster />
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-orange-500" />
              Role Management
            </h1>
            <p className="text-gray-400 mt-2">Create and manage user roles with custom permissions</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} className="bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 text-white border-gray-700">
              <DialogHeader>
                <DialogTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Role Name */}
                {!editingRole && (
                  <div className="space-y-2">
                    <Label htmlFor="name">Role Name (Internal ID)</Label>
                    <Input
                      id="name"
                      placeholder="e.g., technician, supervisor"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                    <p className="text-xs text-gray-400">Lowercase, no spaces (will be auto-formatted)</p>
                  </div>
                )}

                {/* Display Name */}
                <div className="space-y-2">
                  <Label htmlFor="display_name">Display Name</Label>
                  <Input
                    id="display_name"
                    placeholder="e.g., Technician, Supervisor"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Brief description of this role's responsibilities"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    rows={3}
                  />
                </div>

                {/* Permissions */}
                <div className="space-y-4 border-t border-gray-700 pt-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Lock className="w-5 h-5 text-orange-500" />
                    Permissions
                  </h3>
                  
                  {Object.entries(availablePermissions).map(([groupName, permissions]) => (
                    <Card key={groupName} className="bg-gray-800 border-gray-700">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base text-white">{groupName}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {Object.entries(permissions).map(([key, label]) => (
                          <div key={key} className="flex items-center justify-between">
                            <Label htmlFor={key} className="text-sm text-gray-300 cursor-pointer">
                              {label}
                            </Label>
                            <Switch
                              id={key}
                              checked={formData.permissions[key] || false}
                              onCheckedChange={() => togglePermission(key)}
                            />
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
                  Cancel
                </Button>
                <Button onClick={handleSubmit} className="bg-orange-600 hover:bg-orange-700">
                  <Save className="w-4 h-4 mr-2" />
                  {editingRole ? 'Update Role' : 'Create Role'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Roles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roles.map((role) => (
            <Card key={role.id} className="bg-gray-800 border-gray-700 hover:border-orange-500/50 transition-colors">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-orange-500" />
                    <div>
                      <CardTitle className="text-white">{role.display_name}</CardTitle>
                      {role.is_system_role && (
                        <span className="text-xs text-orange-500 font-medium">System Role</span>
                      )}
                    </div>
                  </div>
                  {!role.is_system_role && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleOpenDialog(role)}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(role.id, role.display_name)}
                        className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <CardDescription className="text-gray-400 mt-2">
                  {role.description || 'No description provided'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-sm text-gray-500">Permissions:</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(role.permissions || {})
                      .filter(([_, value]) => value === true)
                      .slice(0, 5)
                      .map(([key]) => (
                        <span
                          key={key}
                          className="text-xs px-2 py-1 rounded bg-orange-500/20 text-orange-300 border border-orange-500/30"
                        >
                          {key.replace(/_/g, ' ')}
                        </span>
                      ))}
                    {Object.values(role.permissions || {}).filter(v => v === true).length > 5 && (
                      <span className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-400">
                        +{Object.values(role.permissions || {}).filter(v => v === true).length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {roles.length === 0 && (
          <Card className="bg-gray-800 border-gray-700 text-center p-12">
            <Shield className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No roles found</h3>
            <p className="text-gray-400 mb-6">Create your first custom role to get started</p>
            <Button onClick={() => handleOpenDialog()} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" />
              Create First Role
            </Button>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default RolesPage;
